/**
 * Subject Entity
 * Represents an academic subject
 *
 * DB Table: Subjects
 * - id (PK) -> bigint
 * - level_id (FK) -> bigint
 * - name -> varchar
 * - description -> text
 */
export interface SubjectProps {
  id: number;
  levelId: number;
  name: string;
  description: string | null;
  isActive: boolean;
  // Computed/aggregated fields (not directly in DB)
  level?: string; // Level name for display
  hours?: number; // From ClassSchedules.duration_minutes
  teacherName?: string; // From course teacher assignment
  modality?: 'ONSITE' | 'ONLINE'; // From course modality
  courseId?: number; // From Courses table - the course linked to this subject
  isDraft?: boolean; // From course is_draft - hidden from students
}

export class Subject {
  private readonly props: SubjectProps;

  constructor(props: SubjectProps) {
    this.props = props;
  }

  get id(): number {
    return this.props.id;
  }

  get levelId(): number {
    return this.props.levelId;
  }

  get name(): string {
    return this.props.name;
  }

  get description(): string | null {
    return this.props.description;
  }

  get isActive(): boolean {
    return this.props.isActive;
  }

  /**
   * Get the level name for display
   */
  get level(): string {
    return this.props.level ?? '';
  }

  /**
   * Get assigned teacher name (from course data)
   * @deprecated This should be fetched from Course entity
   */
  get professor(): string {
    return this.props.teacherName ?? '';
  }

  /**
   * Get weekly hours (from ClassSchedules table)
   * @deprecated This should be fetched from ClassSchedules
   */
  get hours(): number {
    return this.props.hours ?? 0;
  }

  /**
   * Get course modality (ONSITE or ONLINE)
   */
  get modality(): 'ONSITE' | 'ONLINE' | undefined {
    return this.props.modality;
  }

  /**
   * Get the course ID linked to this subject
   */
  get courseId(): number | undefined {
    return this.props.courseId;
  }

  /**
   * Check if the course for this subject is in draft mode
   */
  get isDraft(): boolean {
    return this.props.isDraft ?? false;
  }

  /**
   * Check if subject has a description
   */
  hasDescription(): boolean {
    return this.props.description !== null && this.props.description.length > 0;
  }

  /**
   * Get a short description (first 100 characters)
   */
  getShortDescription(maxLength: number = 100): string {
    if (!this.props.description) return '';
    if (this.props.description.length <= maxLength) return this.props.description;
    return this.props.description.substring(0, maxLength) + '...';
  }

  /**
   * Check if subject belongs to a specific level
   */
  belongsToLevel(levelId: number): boolean {
    return this.props.levelId === levelId;
  }

  toPlainObject(): SubjectProps {
    return { ...this.props };
  }
}
